<script setup lang="ts">
import { secondsToMilliseconds } from 'date-fns';
import { debounce, isNil } from 'lodash-es';
import smoothscroll from 'smoothscroll-polyfill';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { isCosme } from '@/business_specification/20211126-cosme';
import { isPanasonic, shouldHidePriceForPanasonic } from '@/business_specification/20260303-panasonic-hide-zero-price';
import { formatPriceForPanasonic, getPriceRangeForPanasonic } from '@/business_specification/20260601-panasonic-price-display';
import { usePostMessageReceiver } from '@/composables/usePostMessageReceiver';
import { useProductStyle } from '@/composables/useProductStyle';
import { recordProductClicked } from '@/js/api';
import { openNewWindowAfterFastCheckout } from '@/js/browser';
import { tre } from '@/plugins/bugsnag';
import { DEVICE_BREAKPOINT, deviceBreakpoint } from '@/store/breakpoint';
import { SALABLE_STATUS, SKU_COLLAPSE_STYLE, SKU_STYLE_TYPE } from '@/store/constants';
import { buyerPubnubActualId } from '@/store/dynamicGlobalVariables';
import { requestAddToCartNotification } from '@/store/streamPostMessenger';
import { useTemplate } from '@/store/templateData';
import { transformCurrencyToSymbol } from '@/utils/transformer/currency';
import { toFiniteNumberOrNull, toGroupingNumber } from '@/utils/transformer/number';
import { isFiniteNumber } from '@/utils/validators/common';

import Product from '../Product/Product.vue';

// 定義產品相關介面
interface CloudImage {
  id: number;
  url: string;
}

interface ProductSku {
  id: number;
  name: string;
  price: string;
  original_price: string | null;
  salable_quantity: number;
  images: CloudImage[];
}

interface ProductData {
  id: number;
  name: string;
  skus: ProductSku[];
  cover_image_id: number | null;
  images: CloudImage[];
  salable_status: 'before_sell' | 'end_sell' | 'init' | 'on_sell' | 'sold_out';
}


defineOptions({
  name: 'ModuleStreamRoomAndProductSkuDisplay'
});

const SCROLL_OFFSET = 32;

smoothscroll.polyfill();

interface Props {
  module?: {
    components?: {
      title?: {
        text?: string;
      };
      subtitle?: {
        text?: string;
      };
      background?: {
        src?: string;
      };
      name?: {
        textSize?: string;
        lineHeight?: string;
        textColor?: string;
      };
      skuStyle?: {
        type?: string;
        collapseStyle?: string;
      };
      originalPrice?: {
        enabled?: boolean;
        textChoice?: number;
      };
      price?: {
        enabled?: boolean;
        textSize?: string;
        lineHeight?: string;
        textColor?: string;
      };
      shopcart?: {
        enabled?: boolean;
      };
      button?: {
        text?: string;
        textSize?: string;
        textColor?: string;
        lineHeight?: string;
        fillStyle?: string;
      };
    };
  };
}

const props = withDefaults(defineProps<Props>(), {
  module: () => ({
    components: {
      title: {
        text: ''
      },
      subtitle: {
        text: ''
      },
      background: {
        src: ''
      },
      name: {
        textSize: '',
        lineHeight: '',
        textColor: ''
      },
      skuStyle: {
        type: SKU_STYLE_TYPE.EXPAND,
        collapseStyle: SKU_COLLAPSE_STYLE.SQUARE
      },
      originalPrice: {
        enabled: true,
        textChoice: 0
      },
      price: {
        enabled: true,
        textSize: '',
        lineHeight: '',
        textColor: ''
      },
      shopcart: {
        enabled: true
      },
      button: {
        text: '',
        textSize: '',
        textColor: '',
        lineHeight: '',
        fillStyle: 'outline'
      }
    }
  })
});

const { t } = useI18n();
const { handsupConfig, productList, isGeneralCmsStreamCanPlay } = useTemplate();

const slug = computed(() => handsupConfig.value?.store?.slug);
const postId = computed(() => handsupConfig.value?.post?.id);
const currencySymbol = computed(() => {
  const currency = handsupConfig.value?.store?.currency;
  return transformCurrencyToSymbol(currency ?? '');
});

const components = computed(() => props.module.components);

const isShowOriginalPrice = computed(() => components.value?.originalPrice?.enabled ?? true);
const isShowPrice = computed(() => components.value?.price?.enabled ?? true);
const isShowShoppingCart = computed(() => components.value?.shopcart?.enabled ?? true);

const originalPriceDescription = computed(() => {
  const i = components.value?.originalPrice?.textChoice ?? 0;
  // t('cms_setting.display.product.original_price.text.0')
  // t('cms_setting.display.product.original_price.text.1')
  // t('cms_setting.display.product.original_price.text.2')
  // t('cms_setting.display.product.original_price.text.3')
  // t('cms_setting.display.product.original_price.text.4')
  return t(`cms_setting.display.product.original_price.text.${i}`);
});

const toCurrency = (price: string | number | null | undefined) => {
  if (isNil(price) || price === '') {
    return '';
  }

  if (isPanasonic()) {
    if (shouldHidePriceForPanasonic(price)) { return ''; }
    const formatted = toGroupingNumber(price);
    return formatted !== null ? formatPriceForPanasonic(formatted) : '';
  }
  return [currencySymbol.value, toGroupingNumber(price)].join(' ').trim();
};

const toProductButtonDisplay = ({ isBeforeSell = false, isSoldOut = false, isEndSell = false }) => {
  if (isBeforeSell) { return t('button.upcoming_sell'); }
  if (isEndSell) { return t('button.end_sell'); }
  if (isSoldOut) { return t('button.sold_out'); }

  if (isCosme()) { return '商品ページへ'; }

  return components.value?.button?.text || t('button.buy');
};

const isSmallThanLaptop = computed(() => {
  return deviceBreakpoint.value === DEVICE_BREAKPOINT.TABLET ||
    deviceBreakpoint.value === DEVICE_BREAKPOINT.MOBILE;
});

const postMessageReceiver = usePostMessageReceiver();
const productListEl = ref<HTMLElement | null>(null);
const promotingProductIds = ref<number[]>([]);
postMessageReceiver.onPromotingProductChanged((products: Array<{ id: number }> | undefined) => {
  promotingProductIds.value = products?.map((product) => (product.id)) ?? [];
});

const getPriceRange = (priceList: Array<string | number | null>) => {
  const validPrices = priceList.map(toFiniteNumberOrNull).filter(isFiniteNumber) as number[];
  if (validPrices.length === 0) {
    return '';
  }

  if (isPanasonic()) {
    return getPriceRangeForPanasonic(validPrices);
  }

  const minPrice = Math.min(...validPrices);
  const maxPrice = Math.max(...validPrices);

  if (!isNil(minPrice) && !isNil(maxPrice) && minPrice !== maxPrice) {
    return `${currencySymbol.value} ${toGroupingNumber(minPrice)}～${toGroupingNumber(maxPrice)}`;
  }

  if (!isNil(minPrice)) {
    return `${currencySymbol.value} ${toGroupingNumber(minPrice)}`;
  }

  if (!isNil(maxPrice)) {
    return `${currencySymbol.value} ${toGroupingNumber(maxPrice)}`;
  }

  return '';
};

// NOTE: 可能在直播時改價錢造成 instance 跟 remote product 不一致
const selectedProductsSku = ref<Record<number, number | null>>({});

const findAvailableProduct = (product: any) => {
  const selectedSkuId = selectedProductsSku.value[product.id] ?? null;
  const selectedSku = product?.normalizedSkus?.find((sku: any) => sku?.id === selectedSkuId) ?? null;
  // NOTE: 若沒有選擇過，則預設回傳第一個可以購買的 SKU，若都沒有就回傳第一個 SKU
  const availableSku = selectedSku ?? product?.normalizedSkus?.find((sku: any) => sku.salable_quantity > 0);
  return availableSku ?? product?.normalizedSkus[0];
};

const getProductImageURL = (productImages: CloudImage[], productCoverImageId: number | null) => {
  // NOTE: 圖片顯示邏輯(商品封面圖 → 商品圖第一張 → 空字串)
  // https://17media.atlassian.net/wiki/spaces/H/pages/996507649/23Q3+Making+product+images+a+required+field+-#Story-3%EF%BC%9A%E7%95%B6-Seller-%2F-Buyer-%E6%AA%A2%E8%A6%96%E5%95%86%E5%93%81%E6%99%82%EF%BC%8C%E5%95%86%E5%93%81%E5%B1%95%E5%94%AE%E7%9A%84%E5%9C%96%E7%89%87%E6%9C%83%E6%98%AF-seller-%E8%A8%AD%E5%AE%9A%E7%9A%84%E5%95%86%E5%93%81%E5%B0%81%E9%9D%A2%E5%9C%96
  if (productImages.length === 0) { return null; }

  if (productCoverImageId === null) {
    return productImages[0]?.url ?? null;
  }

  const foundProductCoverImage = productImages.find((productImage: CloudImage) => productImage.id === productCoverImageId);
  return foundProductCoverImage?.url ?? productImages[0]?.url ?? null;
};

const normalizedProducts = computed(() =>
  (productList.value as ProductData[]).map((product: ProductData) => {
    const priceRange = getPriceRange(product.skus.map((sku: ProductSku) => sku.price));
    const originalPriceRange = getPriceRange(product.skus.map((sku: ProductSku) => sku.original_price));
    return {
      ...product,
      imageUrl: getProductImageURL(product.images, product.cover_image_id),
      name: product.name,
      priceRange,
      originalPriceRange,
      normalizedSkus: product.skus.map((sku: ProductSku) => ({
        ...sku,
        imageUrl: sku.images[0]?.url ?? '',
      })),
    };
  }),
);

const normalizedProductsDisplay = computed(() => {
  return normalizedProducts.value.map((product: any) => {
    const selectedSkuId = selectedProductsSku.value[product.id] ?? null;
    const selectedSku = product.normalizedSkus.find((sku: any) => sku.id === selectedSkuId) ?? null;
    const priceDisplay = isNil(selectedSku) ? product.priceRange : toCurrency(selectedSku.price);
    const originPriceDisplay = isNil(selectedSku) ? product.originalPriceRange : toCurrency(selectedSku.original_price);

    // TODO: 待討論事項
    // - isBeforeSell、isEndSell、isOnSell 是拿 Product 層級判斷
    // - isSoldOut 是拿 Sku 層級判斷
    // 這樣往下傳至 Component(Product) 是否會有點亂呢？
    const isBeforeSell = product.salable_status === SALABLE_STATUS.BEFORE_SELL;
    const isEndSell = product.salable_status === SALABLE_STATUS.END_SELL;
    const isOnSell = product.salable_status === SALABLE_STATUS.ON_SELL;
    const isSoldOut = (() => {
      if (!isNil(selectedSku)) {
        return selectedSku.salable_quantity <= 0;
      }

      const availableSku = findAvailableProduct(product);
      return availableSku.salable_quantity <= 0;
    })();

    const skuName = (() => {
      if (product.normalizedSkus.length === 1) {
        const firstSku = product.normalizedSkus[0];
        const firstSkuName = firstSku.name;
        if (firstSkuName.toLowerCase() !== 'free' && !firstSku.imageUrl) {
          return firstSkuName;
        }
      }

      if (isNil(selectedSku)) { return ''; }

      const selectedSkuName = selectedSku.name;
      // NOTE: 不顯示 sku 預設名字的 'Free'
      // 目前是拿 Localize 的 Product.DialogAddProduct.default_sku_name 字串，只是剛好目前語系都是 'Free'
      return selectedSkuName.toLowerCase() === 'free' ? '' : selectedSkuName;
    })();

    const shouldHidePanasonicPrice = (() => {
      if (!isNil(selectedSku)) {
        return shouldHidePriceForPanasonic(selectedSku.price);
      }
      // 未選擇 SKU 時：所有 SKU 價格皆為 0 才隱藏
      return product.normalizedSkus.every((sku: ProductSku) => shouldHidePriceForPanasonic(sku.price));
    })();

    return {
      ...product,
      skuName,
      priceDisplay,
      originPriceDisplay,
      isBeforeSell,
      isEndSell,
      isSoldOut,
      isOnSell,
      isPromoting: promotingProductIds.value.includes(product.id),
      buttonName: toProductButtonDisplay({ isBeforeSell, isSoldOut, isEndSell }),
      isShowPrice: isShowPrice.value && !shouldHidePanasonicPrice,
      normalizedSkus: product.normalizedSkus.map((sku: any) => ({
        ...sku,
        isSelected: sku.id === selectedSkuId,
        isSoldOut: sku.salable_quantity === 0,
      })),
    };
  });
});

const iframeEl = ref<HTMLIFrameElement | null>(null);
postMessageReceiver.onStreamReady(() => {
  iframeEl.value = document.querySelector('#HANDSUP_PIP iframe') as HTMLIFrameElement | null;
});

// NOTE: debounce 1 秒，避免連續觸發 API，造成 Server 的壓力
const checkoutShoppingCart = debounce(
  async (product: any) => {
    const availableSku = findAvailableProduct(product);
    // trigger add to cart notice
    if (iframeEl.value) {
      requestAddToCartNotification(availableSku.id);
    }

    if (!postId.value) {
      // TODO: error handling
      return;
    }

    try {
      // 記錄有興趣的點擊事件
      recordProductClicked({
        postId: postId.value!,
        productId: product.id,
        skuId: availableSku.id,
        storeSlug: slug.value!,
        pnActualId: buyerPubnubActualId.value!,
        source: 'cms',
      });
      // 紀錄購買的點擊事件
      (window as any)?.HUT?.trackProductClickEvent?.(availableSku.id);
      // 導轉結帳頁面
      // NOTE: DO NOT USE AWAIT ABOVE
      openNewWindowAfterFastCheckout(postId.value, availableSku.id);
    } catch (error) {
      tre(error);
      // TODO: error handling
    }
  },
  secondsToMilliseconds(1),
  { leading: true },
);

const updateSelectedSkuList = ({ product, sku }: { product: any; sku: any }) => {
  if (selectedProductsSku.value[product.id] === sku.id) {
    selectedProductsSku.value = {
      ...selectedProductsSku.value,
      [product.id]: null,
    };
    return;
  }

  selectedProductsSku.value = {
    ...selectedProductsSku.value,
    [product.id]: sku.id,
  };
};

const needRender = computed(() => [
  isGeneralCmsStreamCanPlay.value,
].some(Boolean));

const { productNameStyle, productPriceStyle, productButtonStyle, skuImageStyle } = useProductStyle(components);

postMessageReceiver.onPromotingProductClicked((product: { id: number } | undefined) => {
  if (!product?.id) {
    return;
  }

  // 記錄有興趣的點擊事件（來源：promoting products）
  const clickedProduct = normalizedProductsDisplay.value.find((item) => item.id === product.id);
  if (clickedProduct && postId.value) {
    const availableSku = findAvailableProduct(clickedProduct);
    if (availableSku) {
      recordProductClicked({
        postId: postId.value,
        productId: clickedProduct.id,
        skuId: availableSku.id,
        storeSlug: slug.value ?? '',
        pnActualId: buyerPubnubActualId.value ?? '',
        source: 'cms',
        entryPoint: 'promoting-products',
      });
    }
  }

  const productEl = document.querySelector(`.product_${product.id}`) as HTMLElement | null;
  if (!productEl || !productListEl.value) {
    return;
  }

  if (isSmallThanLaptop.value) {
    const position = productEl.offsetTop - SCROLL_OFFSET;
    window.scrollTo({ top: position, behavior: 'smooth' });
  } else {
    const position = productEl.offsetTop - (productListEl.value as HTMLElement).offsetTop - SCROLL_OFFSET;
    (productListEl.value as HTMLElement).scrollTo({ top: position, behavior: 'smooth' });
  }
});
</script>

<template>
<div
  class="module-stream-room-and-product-sku-display"
  v-if="needRender"
  ref="productListEl"
>
  <Product
    v-for="product in normalizedProductsDisplay"
    :key="product.id"
    :class="`product_${product.id}`"
    :product="product"
    :isShowOriginalPrice="isShowOriginalPrice"
    :isShowPrice="product.isShowPrice"
    :isShowShoppingCart="isShowShoppingCart"
    :productNameStyle="productNameStyle"
    :productPriceStyle="productPriceStyle"
    :productButtonStyle="productButtonStyle"
    :skuType="SKU_STYLE_TYPE.COLLAPSE"
    :skuImageStyle="skuImageStyle"
    :originalPriceDescription="originalPriceDescription"
    :isShowPanasonicOnlineStoreLabel="isPanasonic()"
    @skuClicked="updateSelectedSkuList"
    @shoppingButtonClicked="checkoutShoppingCart"
  />
</div>
</template>
<style lang="scss" scoped>
$height: 666px;
$image-size-sm: 160px;

.module-stream-room-and-product-sku-display {
  @apply px-5 pt-6 w-full;
  @apply grid grid-cols-2 auto-rows-min;
}

.tablet .module-stream-room-and-product-sku-display {
  @apply px-10;
  @apply grid grid-cols-3;
}

.laptop .module-stream-room-and-product-sku-display {
  @apply px-0 w-auto;
  @apply grid justify-center;
  @apply overflow-auto;

  max-height: $height;
  grid-template-columns: repeat(auto-fill, minmax(0, $image-size-sm));
}

.desktop .module-stream-room-and-product-sku-display {
  @apply px-0 w-auto;
  @apply grid grid-cols-4;
  @apply overflow-auto;

  max-height: $height;
}
</style>
